import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Copy, FileText, TrendingUp, HelpCircle, CheckCircle, AlertCircle, BarChart3, ChevronDown, ChevronRight } from 'lucide-react';
import { summarizationService } from '../services/summarizationService';

const SummaryViewer = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [expandedQuestions, setExpandedQuestions] = useState({});

  useEffect(() => {
    if (jobId) {
      fetchSummaryData();
    }
  }, [jobId]);

  const fetchSummaryData = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching summary data for jobId:', jobId);
      
      let response;
      try {
        // First try the regular endpoint
        response = await summarizationService.getSummarizationResult(jobId);
            } catch (err) {
        throw err; // Re-throw the original error since we want to see actual API failures
      }
      
      console.log('Received API response:', response);
      
      // The actual summary data is nested under the 'result' field
      const summaryResult = response?.result;
      console.log('Extracted summary result:', summaryResult);
      
      if (summaryResult && typeof summaryResult === 'object') {
        console.log('Setting summary data:', summaryResult);
        console.log('Summary data sections:', summaryResult.sections);
        setSummaryData(summaryResult);
      } else if (response?.status === 'completed' && !summaryResult) {
        throw new Error('Summarization completed but no result data available');
      } else if (response?.status === 'processing') {
        throw new Error('Summarization still in progress. Please wait and refresh.');
      } else if (response?.status === 'error') {
        throw new Error(`Summarization failed: ${response?.message || 'Unknown error'}`);
      } else {
        throw new Error(`No summary data available. Status: ${response?.status || 'unknown'}`);
      }
    } catch (err) {
      console.error('Error fetching summary data:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleQuestion = (index) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const downloadSummary = () => {
    if (!summaryData) return;

    let content = `Summary Report - Job ID: ${summaryData.job_id || jobId}\n`;
    content += `Generated: ${new Date().toLocaleString()}\n`;
    content += '\n' + '='.repeat(80) + '\n\n';

    // Call Segmentation - Use the actual sections array
    if (summaryData.sections && Array.isArray(summaryData.sections)) {
      content += 'CALL SEGMENTATION\n';
      content += '='.repeat(80) + '\n\n';
      
      summaryData.sections.forEach((section, index) => {
        const sectionTitle = (section.section_name || section.name || `Section ${index + 1}`)
          .replace(/_/g, ' ')
          .replace(/\b\w/g, l => l.toUpperCase());
        
        content += `${sectionTitle.toUpperCase()}\n`;
        content += '-'.repeat(sectionTitle.length) + '\n';
        
        if (section.summary) {
          // Clean up the summary content for plain text
          const cleanSummary = section.summary
            .replace(/### /g, '')
            .replace(/#### /g, '  ')
            .replace(/\*\*(.*?)\*\*/g, '$1')
            .replace(/\* /g, '• ')
            .replace(/- /g, '• ');
          
          content += `${cleanSummary}\n\n`;
        }
        
        if (section.duration_s) {
          content += `Duration: ${Math.round(section.duration_s / 60)} minutes\n`;
        }
        if (section.financial_alerts_count) {
          content += `Financial Alerts: ${section.financial_alerts_count}\n`;
        }
        if (section.qa_exchanges_count) {
          content += `Q&A Exchanges: ${section.qa_exchanges_count}\n`;
        }
        
        content += '\n' + '='.repeat(80) + '\n\n';
      });
    }

    // Financial Alerts
    if (summaryData.financial_alerts && Array.isArray(summaryData.financial_alerts)) {
      content += 'FINANCIAL ALERTS\n';
      content += '='.repeat(80) + '\n\n';
      
      summaryData.financial_alerts.forEach((alert, index) => {
        content += `${index + 1}. ${alert.metric || alert.title || `Alert ${index + 1}`}\n`;
        if (alert.value) content += `   Value: ${alert.value}\n`;
        if (alert.context) content += `   Context: ${alert.context}\n`;
        if (alert.insight) content += `   Insight: ${alert.insight}\n`;
        content += '\n';
      });
      content += '='.repeat(80) + '\n\n';
    }

    // Q&A Exchanges
    if (summaryData.qa_exchanges && Array.isArray(summaryData.qa_exchanges)) {
      content += 'Q&A EXCHANGES\n';
      content += '='.repeat(80) + '\n\n';
      
      summaryData.qa_exchanges.forEach((qa, index) => {
        content += `Q${index + 1}. ${qa.topic ? `[${qa.topic}] ` : ''}${qa.question || 'No question available'}\n`;
        if (qa.questioner && qa.questioner !== 'Not mentioned') {
          content += `Asked by: ${qa.questioner}\n`;
        }
        content += `\nAnswer: ${qa.answer || 'No answer available'}\n`;
        if (qa.respondent && qa.respondent !== 'Not mentioned') {
          content += `Answered by: ${qa.respondent}\n`;
        }
        content += '\n' + '-'.repeat(60) + '\n\n';
      });
      content += '='.repeat(80) + '\n\n';
    }

    // Summary Statistics
    content += 'SUMMARY STATISTICS\n';
    content += '='.repeat(80) + '\n';
    content += `Total Sections: ${summaryData.sections ? summaryData.sections.length : 0}\n`;
    content += `Financial Alerts: ${summaryData.financial_alerts ? summaryData.financial_alerts.length : 0}\n`;
    content += `Q&A Exchanges: ${summaryData.qa_exchanges ? summaryData.qa_exchanges.length : 0}\n`;
    if (summaryData.last_processed_chunk) {
      content += `Processed Chunks: ${summaryData.last_processed_chunk}\n`;
    }
    if (summaryData.total_duration_s) {
      content += `Total Duration: ${Math.round(summaryData.total_duration_s / 60)} minutes\n`;
    }

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `summary_${jobId}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = async () => {
    if (!summaryData) return;
    
    let textToCopy = JSON.stringify(summaryData, null, 2);
    
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ padding: '32px 0' }}>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '400px',
          color: '#64748b'
        }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '3px solid #e2e8f0',
            borderTop: '3px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '16px'
          }}></div>
          <p>Loading summary...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container" style={{ padding: '32px 0' }}>
        <div className="error-container">
          <div className="error-title">Failed to Load Summary</div>
          <div className="error-message">
            {error.message || 'An error occurred while loading the summary.'}
          </div>
          <button 
            onClick={() => navigate('/summarizations')}
            className="btn btn-primary"
            style={{ marginTop: '16px' }}
          >
            <ArrowLeft style={{ width: '16px', height: '16px' }} />
            Back to Summarizations
          </button>
        </div>
      </div>
    );
  }

  const formatSectionContent = (content) => {
    if (!content) return '';
    
    return content
      // Convert markdown headers to HTML
      .replace(/### (.*?)$/gm, '<h3 style="font-size: 16px; font-weight: 600; color: #1e293b; margin: 16px 0 8px 0; border-bottom: 2px solid #e2e8f0; padding-bottom: 4px;">$1</h3>')
      .replace(/#### (.*?)$/gm, '<h4 style="font-size: 14px; font-weight: 600; color: #374151; margin: 12px 0 6px 0;">$1</h4>')
      
      // Convert markdown bold to HTML
      .replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight: 600; color: #1e293b;">$1</strong>')
      
      // Convert bullet points to proper list items
      .replace(/^\* (.*?)$/gm, '<li style="margin: 4px 0; padding-left: 8px;">$1</li>')
      .replace(/^- (.*?)$/gm, '<li style="margin: 4px 0; padding-left: 8px;">$1</li>')
      
      // Wrap consecutive list items in ul tags
      .replace(/(<li[^>]*>.*?<\/li>\s*)+/gs, '<ul style="margin: 8px 0; padding-left: 20px; list-style-type: disc;">$&</ul>')
      
      // Convert line breaks to proper spacing
      .replace(/\n\n/g, '<br><br>')
      .replace(/\n/g, '<br>')
      
      // Add proper spacing for sections
      .replace(/(<\/h[3-4]>)/g, '$1<div style="margin-bottom: 8px;"></div>')
      
      // Style financial figures and percentages
      .replace(/₹([\d,]+(?:\.\d+)?)\s*(billion|million|crore|lakh)?/g, '<span style="font-weight: 600; color: #059669; background: #dcfce7; padding: 2px 6px; border-radius: 4px;">₹$1 $2</span>')
      .replace(/(\d+(?:\.\d+)?%)/g, '<span style="font-weight: 600; color: #dc2626; background: #fef2f2; padding: 2px 6px; border-radius: 4px;">$1</span>')
      
      // Style US dollar amounts
      .replace(/US\$(\d+(?:\.\d+)?)\s*(million|billion)?/g, '<span style="font-weight: 600; color: #059669; background: #dcfce7; padding: 2px 6px; border-radius: 4px;">US$$1 $2</span>');
  };

  const renderCallSegmentation = () => {
    // Extract sections from the response and organize by name
    const sections = summaryData?.sections || [];
    console.log('Sections data:', sections);
    const sectionsMap = {};
    sections.forEach(section => {
      sectionsMap[section.section_name || section.name] = section.summary;
    });
    console.log('Sections map:', sectionsMap);
    
    const segmentConfigs = [
      {
        key: 'introduction',
        title: 'Introduction',
        icon: '🎯',
        color: '#dbeafe', // blue
        textColor: '#1e40af'
      },
      {
        key: 'management_presentation',
        title: 'Management Presentation',
        icon: '📊',
        color: '#dcfce7', // green
        textColor: '#166534'
      },
      {
        key: 'qa',
        title: 'Q&A Session',
        icon: '❓',
        color: '#fef3c7', // yellow
        textColor: '#92400e'
      },
      {
        key: 'closing_remarks',
        title: 'Closing Remarks',
        icon: '🏁',
        color: '#f3e8ff', // purple
        textColor: '#7c3aed'
      }
    ];

    return (
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ 
          fontSize: '20px', 
          fontWeight: '600', 
          color: '#1e293b',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <FileText style={{ width: '20px', height: '20px' }} />
          Call Segmentation
        </h2>
        
        <div className="grid grid-cols-2" style={{ gap: '16px' }}>
          {segmentConfigs.map((config) => (
            <div
              key={config.key}
              style={{
                background: config.color,
                padding: '20px',
                borderRadius: '12px',
                border: '1px solid rgba(0,0,0,0.1)',
                minHeight: '120px'
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '12px'
              }}>
                <span style={{ fontSize: '18px' }}>{config.icon}</span>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: config.textColor,
                  margin: 0
                }}>
                  {config.title}
                </h3>
              </div>
              
                                           <div style={{
                fontSize: '14px',
                lineHeight: '1.6',
                color: config.textColor,
                maxHeight: '400px',
                overflowY: 'auto',
                paddingRight: '8px'
              }}>
                {sectionsMap[config.key] ? (
                  <div dangerouslySetInnerHTML={{
                    __html: formatSectionContent(sectionsMap[config.key])
                  }} />
                ) : (
                  <div style={{
                    fontStyle: 'italic',
                    color: '#64748b',
                    textAlign: 'center',
                    padding: '20px'
                  }}>
                    No content detected for {config.key.replace('_', ' ')} section.
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderFinancialAlerts = () => {
    const alerts = summaryData?.financial_alerts || [];
    
    if (!Array.isArray(alerts) || alerts.length === 0) {
      return (
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{ 
            fontSize: '20px', 
            fontWeight: '600', 
            color: '#1e293b',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <TrendingUp style={{ width: '20px', height: '20px', color: '#10b981' }} />
            Financial Alerts
            <span style={{ 
              background: '#dcfce7', 
              color: '#166534', 
              fontSize: '12px', 
              padding: '4px 8px', 
              borderRadius: '12px',
              fontWeight: '500'
            }}>
              0 alerts
            </span>
          </h2>
          <div style={{
            background: '#f8fafc',
            padding: '24px',
            borderRadius: '8px',
            textAlign: 'center',
            color: '#64748b'
          }}>
            No financial alerts detected in this summary.
          </div>
        </div>
      );
    }

    return (
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ 
          fontSize: '20px', 
          fontWeight: '600', 
          color: '#1e293b',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <TrendingUp style={{ width: '20px', height: '20px', color: '#10b981' }} />
          Financial Alerts
          <span style={{ 
            background: '#dcfce7', 
            color: '#166534', 
            fontSize: '12px', 
            padding: '4px 8px', 
            borderRadius: '12px',
            fontWeight: '500'
          }}>
            {alerts.length} alerts
          </span>
        </h2>
        
        <div style={{ 
          maxHeight: '500px', 
          overflowY: 'auto',
          display: 'flex', 
          flexDirection: 'column', 
          gap: '16px',
          paddingRight: '8px'
        }}>
          {alerts.map((alert, index) => (
            <div
              key={index}
              style={{
                background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                padding: '16px',
                borderRadius: '12px',
                border: '1px solid #93c5fd',
                boxShadow: '0 2px 4px rgba(59, 130, 246, 0.1)'
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '8px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <BarChart3 style={{ width: '16px', height: '16px', color: '#1d4ed8' }} />
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#1e40af'
                  }}>
                    {alert.metric || alert.title || `Financial Alert ${index + 1}`}
                  </span>
                </div>
                
                {alert.value && (
                  <div style={{
                    background: '#1e40af',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '16px',
                    fontSize: '13px',
                    fontWeight: '600'
                  }}>
                    {alert.value}
                  </div>
                )}
              </div>
              
              <div style={{
                fontSize: '13px',
                lineHeight: '1.4',
                color: '#374151'
              }}>
                💡 {alert.insight || alert.context || alert.content || alert.description || 'No additional details available.'}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderQAExchanges = () => {
    const exchanges = summaryData?.qa_exchanges || [];
    
    if (!Array.isArray(exchanges) || exchanges.length === 0) {
      return (
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{ 
            fontSize: '20px', 
            fontWeight: '600', 
            color: '#1e293b',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <HelpCircle style={{ width: '20px', height: '20px', color: '#8b5cf6' }} />
            Q&A Exchanges
            <span style={{ 
              background: '#f3e8ff', 
              color: '#7c3aed', 
              fontSize: '12px', 
              padding: '4px 8px', 
              borderRadius: '12px',
              fontWeight: '500'
            }}>
              0 questions
            </span>
          </h2>
          <div style={{
            background: '#f8fafc',
            padding: '24px',
            borderRadius: '8px',
            textAlign: 'center',
            color: '#64748b'
          }}>
            No Q&A exchanges detected in this summary.
          </div>
        </div>
      );
    }

    return (
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{ 
          fontSize: '20px', 
          fontWeight: '600', 
          color: '#1e293b',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <HelpCircle style={{ width: '20px', height: '20px', color: '#8b5cf6' }} />
          Q&A Exchanges
          <span style={{ 
            background: '#f3e8ff', 
            color: '#7c3aed', 
            fontSize: '12px', 
            padding: '4px 8px', 
            borderRadius: '12px',
            fontWeight: '500'
          }}>
            {exchanges.length} questions
          </span>
        </h2>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {exchanges.map((qa, index) => {
            const isExpanded = expandedQuestions[index];
            return (
              <div
                key={index}
                style={{
                  background: '#faf5ff',
                  borderRadius: '12px',
                  border: '1px solid #d8b4fe',
                  overflow: 'hidden'
                }}
              >
                {/* Question Header - Clickable */}
                <div 
                  onClick={() => toggleQuestion(index)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '16px',
                    cursor: 'pointer',
                    background: isExpanded ? '#f3e8ff' : 'transparent',
                    borderBottom: isExpanded ? '1px solid #d8b4fe' : 'none',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    flex: 1
                  }}>
                    <div style={{
                      background: '#8b5cf6',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '600',
                      minWidth: 'fit-content'
                    }}>
                      Q{index + 1}
                    </div>
                    <div style={{
                      flex: 1
                    }}>
                      <div style={{
                        fontSize: '12px',
                        fontWeight: '500',
                        color: '#64748b',
                        marginBottom: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        📋 {qa.topic || 'Question Topic'}
                      </div>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#581c87',
                        lineHeight: '1.4'
                      }}>
                        {qa.question || 'No question content available.'}
                      </div>
                    </div>
                  </div>
                  
                  <div style={{
                    marginLeft: '12px',
                    color: '#8b5cf6',
                    transition: 'transform 0.2s ease',
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}>
                    <ChevronDown size={20} />
                  </div>
                </div>

                {/* Answer Section - Collapsible */}
                {isExpanded && (
                  <div style={{
                    padding: '16px',
                    background: 'white'
                  }}>
                    {(qa.questioner && qa.questioner !== 'Not mentioned') && (
                      <div style={{
                        marginBottom: '12px',
                        fontSize: '12px',
                        color: '#64748b',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        👤 Asked by: {qa.questioner}
                      </div>
                    )}
                    
                    <div style={{
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#059669',
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <CheckCircle size={14} />
                      Answer:
                    </div>
                    
                    <div style={{
                      background: '#f0fdf4',
                      padding: '12px',
                      borderRadius: '6px',
                      fontSize: '14px',
                      lineHeight: '1.5',
                      color: '#166534',
                      fontStyle: qa.answer ? 'normal' : 'italic'
                    }}>
                      {qa.answer || 'No answer available'}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="container" style={{ padding: '32px 0' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        marginBottom: '32px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <button 
            onClick={() => navigate('/summarizations')}
            className="btn btn-secondary"
            style={{ marginBottom: '16px' }}
          >
            <ArrowLeft style={{ width: '16px', height: '16px' }} />
            Back to Summarizations
          </button>
          
          <h1 style={{ 
            fontSize: '28px', 
            fontWeight: '700', 
            color: '#1e293b',
            marginBottom: '8px'
          }}>
            Summary Result
          </h1>
          
          <div style={{ 
            color: '#64748b', 
            fontSize: '14px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '24px',
            alignItems: 'center'
          }}>
            <div>
              <strong>Job ID:</strong> {String(summaryData?.job_id || jobId)}
            </div>
            <div>
              <strong>Status:</strong> {String(summaryData?.status || 'Completed')}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            onClick={copyToClipboard}
            className="btn btn-secondary"
            disabled={!summaryData}
          >
            {copied ? (
              <>
                <CheckCircle style={{ width: '16px', height: '16px' }} />
                Copied!
              </>
            ) : (
              <>
                <Copy style={{ width: '16px', height: '16px' }} />
                Copy All
              </>
            )}
          </button>
          
          <button
            onClick={downloadSummary}
            className="btn btn-primary"
            disabled={!summaryData}
          >
            <Download style={{ width: '16px', height: '16px' }} />
            Download
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="card" style={{ padding: '32px' }}>
        {renderCallSegmentation()}
        {renderFinancialAlerts()}
        {renderQAExchanges()}
      </div>
    </div>
  );
};

export default SummaryViewer; 